import Microgame from '../engine/Microgame.js';

export default class UnlockGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Déverrouille !`, en: `Unlock!`, es: `¡Desbloquea!` },
            controls: { fr: `Appuie quand la barre est exactement sur le point`, en: `Tap when the bar is exactly on the dot`, es: `Toca cuando la barra esté exactamente en el punto` },
            objective: { fr: `Crochète les mécanismes de la serrure un par un`, en: `Pick the lock mechanisms one by one`, es: `Fuerza los mecanismos de la cerradura uno por uno` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/><circle cx="12" cy="16" r="1"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'swipe', fromX: 50, fromY: 50, toX: 76, toY: 35, duration: 620 },
                { type: 'wait', duration: 600 },
            ],
            loopDuration: 2600
        };
    }
    constructor(engine) {
        super(engine);
        this.keyAngle = 0;
        this.lockAngle = 0;
        this.isDragging = false;
        this.keyElement = null;
        this.lockElement = null;
        this.startAngle = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "TURN THE KEY!";

        // Center Point
        const cx = '50%';
        const cy = '50%';

        // 1. The Lock (Outer Circle + Keyhole Slot)
        this.lockElement = document.createElement('div');
        this.lockElement.style.position = 'absolute';
        this.lockElement.style.top = cy;
        this.lockElement.style.left = cx;
        this.lockElement.style.transform = 'translate(-50%, -50%)';
        this.lockElement.style.width = '200px';
        this.lockElement.style.height = '200px';
        this.lockElement.style.borderRadius = '50%';
        this.lockElement.style.border = '5px solid #444';
        this.lockElement.style.background = '#222';
        this.lockElement.style.boxShadow = 'inset 0 0 20px #000';
        this.container.appendChild(this.lockElement);

        // Random Target Angle
        this.lockAngle = this.isPlayback ? 30 : (Math.random() * 360);

        // Keyhole Slot (Visual target)
        const slot = document.createElement('div');
        slot.style.position = 'absolute';
        slot.style.top = '50%';
        slot.style.left = '50%';
        slot.style.width = '100px'; // Length of keyhole
        slot.style.height = '20px'; // Width of keyhole
        slot.style.background = this.secondaryColor;
        slot.style.borderRadius = '10px';
        slot.style.transform = `translate(-50%, -50%) rotate(${this.lockAngle}deg)`;
        slot.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        slot.style.opacity = '0.3'; // Dim ghost
        this.lockElement.appendChild(slot);

        // Ghost Head (Direction Indicator)
        // Helps user know which way to point the key
        const ghostHead = document.createElement('div');
        ghostHead.style.position = 'absolute';
        ghostHead.style.right = '0';
        ghostHead.style.top = '50%';
        ghostHead.style.width = '30px';
        ghostHead.style.height = '30px';
        ghostHead.style.borderRadius = '50%';
        ghostHead.style.background = this.secondaryColor;
        ghostHead.style.transform = 'translate(50%, -50%)'; // Center on end
        ghostHead.style.opacity = '0.5';
        slot.appendChild(ghostHead);

        // 2. The Key (User controlled)
        this.keyElement = document.createElement('div');
        this.keyElement.style.position = 'absolute';
        this.keyElement.style.top = cy;
        this.keyElement.style.left = cx;
        this.keyElement.style.width = '240px'; // Slightly larger for handle
        this.keyElement.style.height = '240px';
        this.keyElement.style.transform = 'translate(-50%, -50%) rotate(0deg)';
        this.keyElement.style.pointerEvents = 'none'; // We catch events on screen
        this.container.appendChild(this.keyElement);

        // Key Graphic (Simple bar with handle)
        const keyBar = document.createElement('div');
        keyBar.style.position = 'absolute';
        keyBar.style.top = '50%';
        keyBar.style.left = '50%';
        keyBar.style.width = '120px';
        keyBar.style.height = '16px';
        keyBar.style.background = this.primaryColor;
        keyBar.style.borderRadius = '8px';
        keyBar.style.transform = 'translate(-50%, -50%)'; // Center pivot
        keyBar.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.keyElement.appendChild(keyBar);

        // Handle (Knob)
        const keyHead = document.createElement('div');
        keyHead.style.position = 'absolute';
        keyHead.style.right = '0';
        keyHead.style.top = '50%';
        keyHead.style.width = '40px';
        keyHead.style.height = '40px';
        keyHead.style.borderRadius = '50%';
        keyHead.style.background = this.primaryColor;
        keyHead.style.transform = 'translate(50%, -50%)';
        keyBar.appendChild(keyHead);

        // Interaction Overlay
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    getAngle(x, y) {
        const rect = this.lockElement.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        // atan2 returns radians from -PI to PI. Convert to deg 0-360
        const rad = Math.atan2(y - cy, x - cx);
        let deg = rad * (180 / Math.PI);
        if (deg < 0) deg += 360;
        return deg;
    }

    onDown(e) {
        if (!this.active) return;
        this.isDragging = true;
        // Calculate offset or just set absolute? Absolute is easier for "turning dial"
        const currentAngle = this.getAngle(e.clientX, e.clientY);
        this.keyAngle = currentAngle;
        this.updateKey();
    }

    onMove(e) {
        if (!this.isDragging || !this.active) return;
        this.keyAngle = this.getAngle(e.clientX, e.clientY);
        this.updateKey();
    }

    onUp() {
        if (!this.active) return;
        this.isDragging = false;

        // Validate
        // Normalize angles to 0-360? already done.
        // Difference
        let diff = Math.abs(this.keyAngle - this.lockAngle);
        // Handle wrap around (e.g. 359 vs 1 is diff 2)
        if (diff > 180) diff = 360 - diff;

        if (diff < 30) { // 30 degrees tolerance (Generous)
            this.win();
            // Snap visual
            this.keyAngle = this.lockAngle;
            this.updateKey();
            this.lockElement.style.borderColor = this.primaryColor;
            this.lockElement.style.boxShadow = `0 0 30px ${this.primaryColor}`;
        }
    }

    updateKey() {
        if (this.keyElement) {
            this.keyElement.style.transform = `translate(-50%, -50%) rotate(${this.keyAngle}deg)`;
        }
    }

    update(dt) {
        // time limit
    }

    onTimeout() {
        return false;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
